import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from 'next/image';

import type { CartShipment } from '@eliadmin/core/src/api/cart/cart-types';

import { ShipmentType } from '@eliadmin/core/src/api/cart/cart-types';
import { CartSelectRegion } from '@eliadmin/core/src/components/cart/detail/cart-select-region';
import { CartShipmentProvider } from '@eliadmin/core/src/components/cart/detail/shipment-types/cart-shipment-provider';
import { CustomPlaceShipment } from '@eliadmin/core/src/components/cart/detail/shipment-types/custom-place-shipment';
import { TopTransShipment } from '@eliadmin/core/src/components/cart/detail/shipment-types/top-trans-shipment';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eliadmin/core/src/components/ui/tooltip';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartShipmentDestination } from '@eliadmin/core/src/hooks/cart/use-cart-shipment-destination';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Suspense } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiInfo } from 'react-icons/fi';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

type CartShipmentProps = {
  shipment: CartShipment;
  isLoading?: boolean;
};

// TODO: is active pa yment
export function CartShipment({ shipment, isLoading }: CartShipmentProps) {
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const templateSettings = useGlobalStore(state => state.templateSettings);
  // const locale = useLocaleStore(state => state.currentLocale);

  const form = useFormContext();
  const region = form.watch('region');

  const { t } = useTranslations();
  const { data: cart } = useCart();
  const destination = useCartShipmentDestination();

  const activeShipmentId = cart.shipment?.id;
  const isActiveShipment = String(activeShipmentId) === String(shipment.id);

  // const queryClient = useQueryClient();
  // const isPaymentTypesMutating = queryClient.isMutating({ mutationKey: [CacheKey.CART_PAYMENTS] });
  // const isShipmentTypesMutating = queryClient.isMutating({ mutationKey: [CacheKey.SHIPMENTS] });

  const selectedCountry = form.watch('country.id');

  return (
    <Label
      htmlFor={`shipment-${shipment.id}`}
      className={cn(
        // Column, like core: the pickup-point / carrier widgets render below the shipment row.
        // As a row they were pushed off the right edge of the card on mobile.
        'flex w-full cursor-pointer flex-col items-stretch justify-between gap-3 rounded-[4px] p-3 sm:p-4',
        isActiveShipment && 'bg-primary-foreground',
        'ea-cart-shipment',
      )}
    >
      <div
        className={cn(
          'flex w-full flex-row items-center justify-between gap-4',
          'ea-cart-shipment__content',
        )}
      >
        <div
          className={cn('flex w-full flex-col items-start gap-2', 'ea-cart-shipment__container')}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem
              id={`shipment-${shipment.id}`}
              checked={isActiveShipment}
              value={String(shipment.id)}
              disabled={shipment.price_type === 'distance' && !destination.isComplete}
              // disabled={isShipmentTypesMutating > 0 || isPaymentTypesMutating > 0}
              className={cn('bg-white', 'ea-cart-shipment__radio')}
            />
            {shipment.image && (
              <div
                className={cn(
                  'flex max-h-10 min-h-10 min-w-10 max-w-10 items-center justify-center gap-2 rounded-[4px] bg-white p-0',
                  'ea-cart-shipment__image',
                )}
              >
                <Image
                  alt={resolveImageAlt(shipment?.image, shipment?.name)}
                  src={shipment.image.url}
                  width={0}
                  height={0}
                  className={cn('ea-cart-shipment__image', 'h-7 w-7 object-contain')}
                />
              </div>
            )}
            <div className={cn('flex flex-col gap-1', 'ea-cart-shipment__info')}>
              <div className={cn('flex flex-row items-center gap-2', 'ea-cart-shipment__name')}>
                <span className={cn('h6 font-bold', 'ea-cart-shipment__name-text')}>
                  {shipment.name}
                </span>
                {shipment.description &&
                  !templateSettings.cart?.show_shipment_description?.enabled && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className={cn('', 'ea-cart-shipment__tooltip-trigger')}>
                          <FiInfo className={cn('h-4 w-4', 'ea-cart-shipment__tooltip-icon')} />
                        </TooltipTrigger>
                        <TooltipContent className={cn('', 'ea-cart-shipment__tooltip-content')}>
                          <HtmlRendererRaw html={shipment.description} />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
              </div>
              {shipment.description &&
                templateSettings.cart?.show_shipment_description?.enabled && (
                  <HtmlRendererRaw
                    as="span"
                    className={cn('text-xs text-muted-foreground', 'ea-cart-shipment__description')}
                    html={shipment.description}
                  />
                )}
            </div>
          </div>
          {isActiveShipment && shipment.price_type === 'region' && (
            <div className="ml-8">
              <Suspense fallback={null}>
                <CartSelectRegion shipmentId={shipment.id} />
              </Suspense>
            </div>
          )}
        </div>
        <span
          className={cn(
            // `uppercase` only on the "Zdarma" label: on prices it rendered the currency as "KČ".
            'justify-self-end whitespace-nowrap font-bold',
            'h6',
            'ea-cart-shipment__price',
            shipment.price_with_vat && 'text-black',
            !shipment.price_with_vat && 'uppercase tracking-tight text-[#008000]',
          )}
        >
          {isLoading
            ? '---'
            : shipment.price_type === 'distance'
              ? isActiveShipment
                ? cart.shipment_total != null
                  ? formatPrice(cart.shipment_total, currency, currencySettings)
                  : t('text349')
                : '---'
              : shipment.price_type === 'region' && !!region
                ? isActiveShipment
                  ? cart.shipment_total
                    ? formatPrice(cart.shipment_total, currency, currencySettings)
                    : t('text349')
                  : '---'
                : shipment.price_type === 'dynamic-api'
                  ? shipment.price_with_vat
                    ? formatPrice(shipment.price_with_vat, currency, currencySettings)
                    : t('text1154')
                  : shipment.price_with_vat
                    ? formatPrice(shipment.price_with_vat, currency, currencySettings)
                    : t('text426')}
        </span>
      </div>
      {shipment?.type &&
        isActiveShipment &&
        // add external provider type here
        [ShipmentType.PACKETA, ShipmentType.GLS_ZBOX, ShipmentType.SPS_ZBOX].includes(
          shipment.type,
        ) && (
          <CartShipmentProvider
            shipment={
              shipment as Extract<
                CartShipment,
                { type: ShipmentType.PACKETA | ShipmentType.GLS_ZBOX | ShipmentType.SPS_ZBOX }
              >
            }
            selectedCountry={selectedCountry}
          />
        )}
      {shipment?.type && isActiveShipment && shipment.type === ShipmentType.CUSTOM_PLACE && (
        <CustomPlaceShipment
          shipment={shipment as Extract<CartShipment, { type: ShipmentType.CUSTOM_PLACE }>}
        />
      )}
      {shipment?.type && isActiveShipment && shipment.type === ShipmentType.TOP_TRANS && (
        <TopTransShipment
          shipment={shipment as Extract<CartShipment, { type: ShipmentType.TOP_TRANS }>}
        />
      )}
    </Label>
  );
}
